import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Globe, ExternalLink, Loader2, Rss, FileText, Activity, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import PublicationSubscriptionsTab from '../components/feeds/PublicationSubscriptionsTab';
import PublicationItemsTab from '../components/feeds/PublicationItemsTab';
import PublicationOverviewTab from '../components/feeds/PublicationOverviewTab';
import PublicationStoriesTab from '../components/feeds/PublicationStoriesTab';
import PublicationMetricsTab from '../components/feeds/PublicationMetricsTab';

const fmtUKDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short',
  });
};


export default function FeedsPublicationDetailPage() {
  const { pubId }  = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [publication, setPublication] = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => { load(); }, [pubId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/feeds/overview');
      const pub = data.publications.find(p => p.id === pubId);
      setPublication(pub);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => setSearchParams({ tab });

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return <div className="text-center py-16"><p className="text-slate-400">You do not have access to EventFlow.</p></div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading...
      </div>
    );
  }

  if (!publication) {
    return <div className="text-center py-16 text-slate-400 text-sm">Publication not found.</div>;
  }

  const tabs = [
    { id: 'overview',      label: 'Overview',      icon: Activity },
    { id: 'subscriptions', label: 'Subscriptions', icon: Rss,      count: publication.total_subscriptions },
    { id: 'items',         label: 'Feed',          icon: FileText },
    { id: 'stories',       label: 'Generated',     icon: Globe,    count: publication.stories_published },
    { id: 'metrics',       label: 'Metrics',       icon: BarChart3 },
  ];

  return (
    <div>
      <button
        onClick={() => navigate('/feeds')}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors"
      >
        <ArrowLeft size={14} /> Back to EventFlow
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Globe size={16} className="text-slate-400" />
              <h1 className="text-xl font-bold text-slate-900">{publication.name}</h1>
              {publication.site_type && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">
                  {publication.site_type}
                </span>
              )}
            </div>
            {publication.domain && (
              <a
                href={`https://${publication.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors"
              >
                {publication.domain} <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-200">
          <Stat label="Total subscriptions" value={publication.total_subscriptions || 0} />
          <Stat label="Active" value={publication.active_subscriptions || 0} />
          <Stat label="Stories published" value={publication.stories_published || 0} highlight={publication.stories_published > 0} />
          <Stat label="Last fetch" value={fmtUKDate(publication.latest_fetch_at)} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 mb-6 w-fit overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                  activeTab === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview'      && <PublicationOverviewTab pubId={pubId} publication={publication} />}
      {activeTab === 'subscriptions' && <PublicationSubscriptionsTab pubId={pubId} onChange={load} />}
      {activeTab === 'items'         && <PublicationItemsTab pubId={pubId} />}
      {activeTab === 'stories'       && <PublicationStoriesTab pubId={pubId} pubDomain={publication.domain} />}
      {activeTab === 'metrics'       && <PublicationMetricsTab pubId={pubId} />}
    </div>
  );
}


function Stat({ label, value, highlight }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${highlight ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}